import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import {
  companyAdminListType,
  LegalEntityAdminCreateSchema,
  legalEntityUpdateSituation
} from "../types";
import { companyAdminNewListType, CompanyResponse } from "@/features/company/types";
import { AdminApiClient } from "../api-client/api-client";

export const useAdmin = () => {
  const companiesAdminQuery = (page?: number, limit?: number, legal_name?: string) =>
    useQuery<{ companyList: companyAdminListType[]; hasMore: boolean; count: number }>({
      queryKey: [legal_name ? `companies_admin_${legal_name}` : `companies_admin`, page, limit],
      queryFn: async () => {
        try {
          const data = await AdminApiClient.fetchAdminListOfCompanies(page, limit, legal_name);
          if (!data.success) throw new Error();
          return {
            companyList: data.data,
            hasMore: data.hasMore,
            count: data.count
          };
        } catch (_) {
          throw new Error("Error get companies");
        }
      },

      retryOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      placeholderData: keepPreviousData
    });
  const addCompanyDataAdminMutation = () =>
    useMutation<CompanyResponse, Error, LegalEntityAdminCreateSchema>({
      mutationFn: async (data) => await AdminApiClient.addAdminCompanyInfo(data)
    });
  const editCompanySituationMutation = () =>
    useMutation<
      { success: true; data: companyAdminNewListType },
      Error,
      legalEntityUpdateSituation
    >({
      mutationFn: async (data) => await AdminApiClient.editCompanySituation(data)
    });
  const companiesAdminNewQuery = (
    showAll: boolean,
    page?: number,
    limit?: number,
    legal_name?: string
  ) =>
    useQuery<{ companyList: companyAdminNewListType[]; hasMore: boolean; count: number }>({
      queryKey: legal_name
        ? showAll
          ? [`companies_admin_new`, page, limit, legal_name]
          : [`companies_admin_new`, "all", page, limit, legal_name]
        : showAll
          ? [`companies_admin_new`, "all", page, limit]
          : [`companies_admin_new`, page, limit],
      queryFn: async () => {
        try {
          const data = await AdminApiClient.fetchAdminNewListOfCompanies(
            showAll,
            page,
            limit,
            legal_name
          );
          if (!data.success) throw new Error();
          return {
            companyList: data.data,
            hasMore: data.hasMore,
            count: data.count
          };
        } catch (_) {
          throw new Error("Error get companies");
        }
      },

      retryOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      placeholderData: keepPreviousData
    });

  return {
    companiesAdminQuery,
    addCompanyDataAdminMutation,
    companiesAdminNewQuery,
    editCompanySituationMutation
  };
};
